import {
  Button,
  Form,
  message,
  Modal,
  Input,
  Upload,
  DatePicker,
  Row,
  Col,
  Space,
  Tag,
  Descriptions,
  Timeline,
} from "antd";
import React, { useEffect, useState } from "react";
import ComplainceService from "Services/AdminServices/ComplainceService";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  UploadOutlined,
  HistoryOutlined,
  PlusOutlined,
  DownloadOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";

function useComplaince() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [versionModalVisible, setVersionModalVisible] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [confirmVersionModalVisible, setConfirmVersionModalVisible] = useState(false);
  const [addloading, setAddLoading] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [policyDetails, setPolicyDetails] = useState(null);
  const [versionLoading, setVersionLoading] = useState(false);
  const [pendingPolicyData, setPendingPolicyData] = useState(null);
  const [pendingVersionData, setPendingVersionData] = useState(null);
  const [versionForm] = Form.useForm();
  const [policyForm] = Form.useForm();
  const [addPolicyModalVisible, setAddPolicyModalVisible] = useState(false);

  // Fetch compliance data
  const fetchComplainceData = async () => {
    setLoading(true);
    try {
      const response = await ComplainceService.fetchComplainces();
      setData(response || []);
    } catch (error) {
      console.error("Error fetching compliance data:", error);
      message.error("Failed to fetch compliance data");
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission for new policy - Show confirmation first
  const handleSubmit = async (values) => {
    setPendingPolicyData(values);
    setConfirmModalVisible(true);
  };

  const handleConfirmSubmit = async () => {
    if (!pendingPolicyData) return;

    try {
      setAddLoading(true);
      setConfirmModalVisible(false);

      if (!pendingPolicyData.PolicyDocumentFile || !pendingPolicyData.PolicyDocumentFile.length) {
        message.error("Please upload a policy document file");
        return;
      }

      if (!pendingPolicyData.ExpiryDate) {
        message.error("Please select an expiry date");
        return;
      }

      await ComplainceService.addComplaince(pendingPolicyData);

      message.success("Compliance policy added successfully");
      policyForm.resetFields();
      setPendingPolicyData(null);
      setAddPolicyModalVisible(false);
      fetchComplainceData();
    } catch (error) {
      console.error("Error adding compliance:", error);

      if (error.response?.status === 400) {
        const errorData = error.response.data;
        if (errorData.errors) {
          const validationErrors = Object.values(errorData.errors).flat();
          message.error(`Validation errors: ${validationErrors.join(", ")}`);
        } else {
          message.error(errorData.title || "Validation failed. Please check your input.");
        }
      } else {
        message.error("Failed to add compliance policy");
      }
    } finally {
      setAddLoading(false);
    }
  };

  const handleAddVersion = (policy) => {
    const policyId = policy.complianceId || policy.id;

    if (!policy || !policyId) {
      console.error("Invalid policy object:", policy);
      message.error("No valid policy selected for versioning");
      return;
    }

    setSelectedPolicy({
      ...policy,
      complianceId: policyId,
    });
    setVersionModalVisible(true);
    versionForm.setFieldsValue({
      EffectiveDate: dayjs(),
      ExpiryDate: policy.expiryDate ? dayjs(policy.expiryDate) : null,
    });
  };

  // Submit new version - Show confirmation first
  const handleVersionSubmit = async (values) => {
    if (!selectedPolicy) {
      message.error("No policy selected for versioning");
      return;
    }

    // Get the correct ID field
    const policyId = selectedPolicy.complianceId || selectedPolicy.id;

    if (!policyId) {
      message.error("No valid policy ID found for versioning");
      return;
    }

    if (!values.PolicyDocumentFile || !values.PolicyDocumentFile.length) {
      message.error("Please upload a new version file");
      return;
    }

    // Store version data and show confirmation
    const versionData = {
      ComplianceId: policyId, // Use the correct ID
      PolicyDocumentFile: values.PolicyDocumentFile,
      EffectiveDate: values.EffectiveDate,
      ExpiryDate: values.ExpiryDate,
    };

    setPendingVersionData(versionData);
    setConfirmVersionModalVisible(true);
  };

  // Actually submit version after confirmation
  const handleConfirmVersionSubmit = async () => {
    if (!pendingVersionData) return;

    try {
      setVersionLoading(true);
      setConfirmVersionModalVisible(false);

      await ComplainceService.addPolicyVersion(pendingVersionData);

      message.success("New version added successfully");
      versionForm.resetFields();
      setVersionModalVisible(false);
      setSelectedPolicy(null);
      setPendingVersionData(null);
      fetchComplainceData();
    } catch (error) {
      console.error("Error adding version:", error);

      if (error.response?.status === 400) {
        const errorData = error.response.data;
        console.error("Validation errors details:", errorData);

        if (errorData.errors) {
          const validationErrors = Object.values(errorData.errors).flat();
          message.error(`Validation errors: ${validationErrors.join(", ")}`);
        } else {
          message.error(errorData.title || "Validation failed. Please check your input.");
        }
      } else {
        message.error("Failed to add new version");
      }
    } finally {
      setVersionLoading(false);
    }
  };

  // Handle view details
  const handleViewDetails = async (compliance) => {
    try {
      // Check if we have valid data
      if (!compliance || (!compliance.policyId && !compliance.id)) {
        message.error("Invalid policy data");
        return;
      }

      const complianceId = compliance.id;
      const details = await ComplainceService.getPolicyWithVersions(complianceId);

      // Check if we got valid response
      if (!details) {
        message.error("No policy details found");
        return;
      }

      setPolicyDetails(details);
      setDetailsModalVisible(true);
    } catch (error) {
      console.error("Error fetching policy details:", error);
      message.error("Failed to load policy details");
    }
  };

  const handleDownload = async (versionId) => {
    try {
      const response = await ComplainceService.downloadPolicy(versionId);

      // Check if we have valid response data
      if (!response.data) {
        message.error("No file data received");
        return;
      }

      // Create blob from response data
      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/pdf",
      });

      // Check blob size
      if (blob.size === 0) {
        message.error("Received empty file");
        return;
      }

      // Create download URL
      const url = window.URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement("a");
      link.href = url;

      // Get filename from response headers or use default
      let filename = `policy-version-${versionId}.pdf`;
      const contentDisposition = response.headers["content-disposition"];

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      window.URL.revokeObjectURL(url);

      message.success(`Downloaded: ${filename}`);
    } catch (error) {
      console.error("Error downloading file:", error);

      // More specific error messages
      if (error.response?.status === 404) {
        message.error("File not found on server");
      } else if (error.response?.status === 500) {
        message.error("Server error while downloading file");
      } else {
        message.error("Failed to download file");
      }
    }
  };

  // Fetch data on mount
  useEffect(() => {
    fetchComplainceData();
  }, []);

  // Add Policy Modal
  const AddPolicyModal = () => (
    <Modal
      title={
        <div>
          <PlusOutlined style={{ marginRight: 8, color: "#1890ff" }} />
          Add New Compliance Policy
        </div>
      }
      open={addPolicyModalVisible}
      onCancel={() => {
        policyForm.resetFields();
        setAddPolicyModalVisible(false);
        setPendingPolicyData(null);
      }}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Form
        form={policyForm}
        onFinish={handleSubmit}
        layout="vertical"
        encType="multipart/form-data"
      >
        <Row gutter={[24, 16]}>
          <Col span={12}>
            <Form.Item
              label={<b style={{ color: "#262626" }}>Policy Name</b>}
              name="PolicyName"
              rules={[{ required: true, message: "Please enter policy name" }]}
            >
              <Input placeholder="Enter Policy Name" size="large" style={{ borderRadius: "6px" }} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={<b style={{ color: "#262626" }}>Expiry Date</b>}
              name="ExpiryDate"
              rules={[{ required: true, message: "Please select expiry date" }]}
            >
              <DatePicker
                style={{ width: "100%", borderRadius: "6px" }}
                size="large"
                format="YYYY-MM-DD"
                disabledDate={(current) => current && current < dayjs().startOf("day")}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[24, 16]}>
          <Col span={12}>
            <Form.Item label={<b style={{ color: "#262626" }}>Description</b>} name="Description">
              <Input.TextArea
                placeholder="Enter policy description (optional)"
                rows={4}
                style={{ borderRadius: "6px" }}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={<b style={{ color: "#262626" }}>Upload Policy File</b>}
              name="PolicyDocumentFile"
              valuePropName="fileList"
              getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
              rules={[{ required: true, message: "Please upload a policy file" }]}
            >
              <Upload
                beforeUpload={() => false}
                accept=".pdf,.docx,.doc"
                maxCount={1}
                listType="text"
              >
                <Button icon={<UploadOutlined />} size="large" style={{ borderRadius: "6px" }}>
                  Select Policy File
                </Button>
              </Upload>
            </Form.Item>
            <div style={{ fontSize: "12px", color: "#8c8c8c", marginTop: "-8px" }}>
              Supported formats: PDF, DOC, DOCX
            </div>
          </Col>
        </Row>

        <Form.Item style={{ marginTop: "24px", marginBottom: 0, textAlign: "right" }}>
          <Space>
            <Button
              onClick={() => {
                policyForm.resetFields();
        setAddPolicyModalVisible(false);
        setPendingPolicyData(null);
              }}
              size="large"
            >
              Cancel
            </Button>
            <Button
              className="btn-primary"
              htmlType="submit"
              loading={addloading}
              size="large"
              style={{
                borderRadius: "6px",
                minWidth: "100px",
              }}
            >
              {addloading ? "Submitting..." : "Submit"}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );

  // Add Version Modal
  const VersionModal = () => (
    <Modal
     title={
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <PlusOutlined style={{ color: "var(--primary-main)" }} />
    <span style={{ color: "var(--text-main)", fontWeight: 600 }}>
      Add New Version: {selectedPolicy?.policyName}
    </span>
    {selectedPolicy && (
      <Tag 
        bordered={false} 
        style={{ 
          backgroundColor: "var(--light-main)", 
          color: "var(--primary-focus)",
          borderRadius: '4px' 
        }}
      >
        ID: {selectedPolicy.complianceId || selectedPolicy.id}
      </Tag>
    )}
  </div>
}
      open={versionModalVisible}
      onCancel={() => {
        setVersionModalVisible(false);
        setSelectedPolicy(null);
        versionForm.resetFields();
      }}
      footer={null}
      width={700}
      destroyOnClose
    >
      <Form form={versionForm} onFinish={handleVersionSubmit} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={
                <span>
                  <CalendarOutlined style={{ marginRight: 4 }} />
                  Effective Date
                </span>
              }
              name="EffectiveDate"
              rules={[{ required: true, message: "Please select effective date" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                disabledDate={(current) => current && current < dayjs().startOf("day")}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={
                <span>
                  <CalendarOutlined style={{ marginRight: 4 }} />
                  Expiry Date
                </span>
              }
              name="ExpiryDate"
              rules={[{ required: true, message: "Please select expiry date" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                disabledDate={(current) => current && current < dayjs().startOf("day")}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Upload New Version File"
          name="PolicyDocumentFile"
          valuePropName="fileList"
          getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
          rules={[{ required: true, message: "Please upload the new version file" }]}
          extra="This will replace the existing policy document with the new version"
        >
          <Upload beforeUpload={() => false} accept=".pdf,.docx,.doc" maxCount={1}>
            <Button icon={<UploadOutlined />} className="btn-primary">
              Select New Version File
            </Button>
          </Upload>
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
          <Space>
            <Button
            className="btn-cancel"
              onClick={() => {
                setVersionModalVisible(false);
                setSelectedPolicy(null);
                versionForm.resetFields();
              }}
            >
              Cancel
            </Button>
            <Button
              className="btn-primary"
              htmlType="submit"
              loading={versionLoading}
              style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
            >
              Add Version
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );

  // Policy Details Modal
  const DetailsModal = () => (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", fontSize: 18, fontWeight: 600 }}>
          <InfoCircleOutlined style={{ marginRight: 12, fontSize: 20, color: "#599c16" }} />
          Policy Details:
          <span style={{ marginLeft: 12, color: "#333" }}>{policyDetails?.policyName}</span>
        </div>
      }
      open={detailsModalVisible}
      onCancel={() => {
        setDetailsModalVisible(false);
        setPolicyDetails(null);
      }}
      footer={
        <div style={{ textAlign: "right", paddingTop: 12 }}>
          <Button
            type="default"
            onClick={() => {
              setDetailsModalVisible(false);
              setPolicyDetails(null);
            }}
            style={{
              minWidth: 110,
              background: "#599c16",
              color: "white",
              borderRadius: 6,
              fontWeight: 500,
            }}
          >
            Close
          </Button>
        </div>
      }
      width={1100}
      style={{ left: "80px" }}
      styles={{
        body: {
          maxHeight: "calc(90vh - 108px)",
          overflowY: "auto",
          padding: "24px 28px",
          background: "#fafafa",
        },
      }}
      destroyOnClose
    >
      {policyDetails && (
        <div style={{ display: "flex", flexDirection: "column", gap: 36 }}>
          {/* --- Policy Info Section --- */}
          <section>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 20,
                paddingBottom: 12,
                borderBottom: "2px solid #e8e8e8",
              }}
            >
              <FileTextOutlined style={{ marginRight: 10, fontSize: 18, color: "#599c16" }} />
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1b1b1b" }}>
                Policy Information
              </h3>
            </div>

            <Descriptions
              bordered
              column={2}
              size="middle"
              className="descriptions-professional"
            >
              <Descriptions.Item label="Policy Name" span={2}>
                <strong>{policyDetails.policyName}</strong>
              </Descriptions.Item>

              <Descriptions.Item label="Description" span={2}>
                <div
                  style={{
                    maxHeight: 120,
                    overflowY: "auto",
                    padding: "4px 0",
                  }}
                >
                  {policyDetails.description || (
                    <span style={{ color: "#64748b", fontStyle: "italic" }}>
                      No description provided
                    </span>
                  )}
                </div>
              </Descriptions.Item>

              <Descriptions.Item label="Status">
                <Tag color="#599c16" style={{ fontWeight: 500 }}>
                  Active
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item label="Created On">
                <CalendarOutlined style={{ marginRight: 8, color: "#599c16" }} />
                {policyDetails.createdOn
                  ? dayjs(policyDetails.createdOn).format("MMMM DD, YYYY HH:mm")
                  : "Unknown"}
              </Descriptions.Item>

              <Descriptions.Item label="Last Modified">
                <CalendarOutlined style={{ marginRight: 8, color: "#599c16" }} />
                {policyDetails.lastModifiedOn
                  ? dayjs(policyDetails.lastModifiedOn).format("MMMM DD, YYYY HH:mm")
                  : "Unknown"}
              </Descriptions.Item>
            </Descriptions>
          </section>

          {/* --- Version History Section --- */}
          <section>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 20,
                paddingBottom: 12,
                borderBottom: "2px solid #e8e8e8",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <HistoryOutlined style={{ marginRight: 10, fontSize: 18, color: "#599c16" }} />
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1b1b1b" }}>
                  Version History
                </h3>
              </div>
              <Tag style={{ fontWeight: 500, padding: "4px 10px" }}>
                {policyDetails.versions?.length || 0} versions
              </Tag>
            </div>

            {policyDetails.versions?.length > 0 ? (
              <div style={{ maxHeight: 320, overflowY: "auto", paddingRight: 10 }}>
                <Timeline
                  style={{ marginLeft: 16 }}
                  items={policyDetails.versions.map((version, index) => ({
                    key: version.id ?? index,
                    dot: version.isActive ? (
                      <CheckCircleOutlined style={{ fontSize: 16, color: "#599c16" }} />
                    ) : (
                      <FileTextOutlined style={{ fontSize: 14 }} />
                    ),
                    children: (
                      <div
                        style={{
                          background: "#fff",
                          padding: "16px 20px",
                          borderRadius: 8,
                          border: "1px solid #e8e8e8",
                        }}
                      >
                        {/* Version Header */}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 12,
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <strong>Version {policyDetails.versions.length - index}</strong>
                            {version.isActive && (
                              <Tag color="#599c16" style={{ fontWeight: 500 }}>
                                Active
                              </Tag>
                            )}
                          </div>
                        </div>

                        {/* Version Details */}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 12,
                            marginBottom: 16,
                            fontSize: 13,
                          }}
                        >
                          <div>
                            <span style={{ fontWeight: 600, color: "#595959" }}>
                              <CalendarOutlined style={{ marginRight: 6 }} />
                              Effective Date:
                            </span>{" "}
                            {version.effectiveDate
                              ? dayjs(version.effectiveDate).format("MMM DD, YYYY")
                              : "N/A"}
                          </div>
                        </div>

                        {/* Download Button */}
                        <Button
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={() => handleDownload(version.id)}
                          style={{
                            borderRadius: 6,
                            background: "#599c16",
                            color: "white",
                            fontWeight: 500,
                          }}
                        >
                          Download
                        </Button>
                      </div>
                    ),
                  }))}
                />
              </div>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 20px",
                  color: "#6c757d",
                  background: "#f8f9fa",
                  borderRadius: 8,
                  border: "1px dashed #dee2e6",
                }}
              >
                <FileTextOutlined style={{ fontSize: 32, marginBottom: 12, color: "#adb5bd" }} />
                <div style={{ fontSize: 14, fontWeight: 500 }}>No versions available</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>This policy has no version history</div>
              </div>
            )}
          </section>
        </div>
      )}
    </Modal>
  );

  // Confirmation Modal for Add Policy
  const ConfirmPolicyModal = () => (
    <Modal
      title={
        <div>
          <ExclamationCircleOutlined style={{ marginRight: 8, color: "#faad14" }} />
          Confirm Add Policy
        </div>
      }
      open={confirmModalVisible}
      onCancel={() => setConfirmModalVisible(false)}
      footer={
        <Space>
          <Button onClick={() => setConfirmModalVisible(false)}>Cancel</Button>
          <Button
            className="btn-primary"
            loading={addloading}
            onClick={handleConfirmSubmit}
            style={{ backgroundColor: "#3d556cff", borderColor: "#1890ff" }}
          >
            Yes, Add Policy
          </Button>
        </Space>
      }
      width={500}
    >
      <div style={{ padding: "16px 0" }}>
        <p>
          <strong>Are you sure you want to add this new policy?</strong>
        </p>
        {pendingPolicyData && (
          <div
            style={{
              background: "#f5f5f5",
              padding: "12px",
              borderRadius: "6px",
              marginTop: "12px",
            }}
          >
            <p>
              <strong>Policy Name:</strong> {pendingPolicyData.PolicyName}
            </p>
            <p>
              <strong>Expiry Date:</strong>{" "}
              {pendingPolicyData.ExpiryDate
                ? pendingPolicyData.ExpiryDate.format("MMMM DD, YYYY")
                : "Not specified"}
            </p>
            <p>
              <strong>Description:</strong> {pendingPolicyData.Description || "No description"}
            </p>
            <p>
              <strong>File:</strong>{" "}
              {pendingPolicyData.PolicyDocumentFile?.[0]?.name || "No file selected"}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );

  // Confirmation Modal for Add Version
  const ConfirmVersionModal = () => (
    <Modal
      title={
        <div>
          <ExclamationCircleOutlined style={{ marginRight: 8, color: "#faad14" }} />
          Confirm Add Version
        </div>
      }
      open={confirmVersionModalVisible}
      onCancel={() => setConfirmVersionModalVisible(false)}
      footer={
        <Space>
          <Button onClick={() => setConfirmVersionModalVisible(false)}>Cancel</Button>
          <Button
            className="btn-primary"
            loading={versionLoading}
            onClick={handleConfirmVersionSubmit}
            style={{ backgroundColor: "#3d556cff", borderColor: "#e6ebefff" }}
          >
            Yes, Add Version
          </Button>
        </Space>
      }
      width={500}
    >
      <div style={{ padding: "16px 0" }}>
        <p>
          <strong>Are you sure you want to add a new version for this policy?</strong>
        </p>
        {pendingVersionData && selectedPolicy && (
          <div
            style={{
              background: "#f5f5f5",
              padding: "12px",
              borderRadius: "6px",
              marginTop: "12px",
            }}
          >
            <p>
              <strong>Policy:</strong> {selectedPolicy.policyName}
            </p>
            <p>
              <strong>Effective Date:</strong>{" "}
              {pendingVersionData.EffectiveDate
                ? pendingVersionData.EffectiveDate.format("MMMM DD, YYYY")
                : "Not specified"}
            </p>
            <p>
              <strong>Expiry Date:</strong>{" "}
              {pendingVersionData.ExpiryDate
                ? pendingVersionData.ExpiryDate.format("MMMM DD, YYYY")
                : "Not specified"}
            </p>
            <p>
              <strong>File:</strong>{" "}
              {pendingVersionData.PolicyDocumentFile?.[0]?.name || "No file selected"}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );

  // Table Columns
  const columns = [
    {
      title: "Policy Name",
      dataIndex: "policyName",
      key: "policyName",
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          {record.version && (
            <Tag color="blue" style={{ fontSize: "10px", marginTop: "2px" }}>
              v{record.version}
            </Tag>
          )}
        </div>
      ),
    },

    {
      title: "Effective Date",
      dataIndex: "effectiveDate",
      key: "effectiveDate",
      render: (date) => (date ? dayjs(date).format("MMM DD, YYYY") : "-"),
    },
    {
      title: "Expiry Date",
      dataIndex: "expiryDate",
      key: "expiryDate",
      render: (date) => (date ? dayjs(date).format("MMM DD, YYYY") : "-"),
    },

    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Button
            className="btn-primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleAddVersion(record);
            }}
            style={{
              backgroundColor: "#3d556cff",
              borderColor: "#e6ebefff",
              fontSize: "12px",
            }}
          >
            New Version
          </Button>
          <Button
            className="btn-primary"
            size="small"
            icon={<InfoCircleOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleViewDetails(record);
            }}
            style={{
              backgroundColor: "#3d556cff",
              borderColor: "#e6ebefff",
              fontSize: "12px",
            }}
          >
            Details
          </Button>
        </Space>
      ),
    },
  ];

  return {
    columns,
    handleSubmit,
    data,
    loading,
    addloading,
    policyForm,
    handleAddVersion,
    AddPolicyModal,
    VersionModal,
    DetailsModal,
    ConfirmPolicyModal,
    ConfirmVersionModal,
    addPolicyModalVisible,
    setAddPolicyModalVisible,
  };
}

export default useComplaince;
